import { useEffect, useMemo, useState } from "react";

const DURATION = 1400;

// "~247 mil" -> prefixo "~", número 247, sufixo " mil". Valores sem número (ou com mais de um) ficam como estão.
function parse(value: string) {
  const m = value.match(/^(\D*?)(\d+(?:[.,]\d+)?)(\D*)$/);
  if (!m) return null;
  const [, prefix, digits, suffix] = m;
  const sep = digits.includes(",") ? "," : ".";
  const decimals = digits.split(/[.,]/)[1]?.length ?? 0;
  return { prefix, suffix, sep, decimals, target: parseFloat(digits.replace(",", ".")) };
}

// Número que conta do zero até o valor quando o bloco aparece. `state` é o sufixo de classe do useInView:
// sem animação mostra o valor final; esperando mostra zero; visível conta. Leitores de tela leem só o valor final.
export default function CountUp({ value, state }: { value: string; state: string }) {
  const parsed = useMemo(() => parse(value), [value]);
  const waiting = state.includes("is-animated") && !state.includes("is-visible");
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!parsed || !state.includes("is-visible")) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / DURATION, 1);
      setCurrent(parsed.target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [parsed, state]);

  if (!parsed || !state.includes("is-animated")) return <>{value}</>;

  const n = waiting ? 0 : current;
  const shown = `${parsed.prefix}${n.toFixed(parsed.decimals).replace(".", parsed.sep)}${parsed.suffix}`;
  return (
    <>
      <span aria-hidden="true">{shown}</span>
      <span className="cz-sr-only">{value}</span>
    </>
  );
}
